import { type MouseEvent, useRef } from 'react'
import styled from 'styled-components'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import {
  useIsGuestUser,
  useIsUserZipOnlySignedInUser,
  useIsPickup,
  useStoreId,
  useUserStoreName,
} from '@/features/account/services/User/hooks'
import { CaretDownIcon } from '@shipt/design-system-icons'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import { DeliveryPickupSwitch } from '@/features/shared/components/HamburgerMenu/DeliveryPickupSwitch'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { TextButton } from '@shipt/design-system-buttons'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicChooseStoreModal } from '@/features/account/components/Modal/ChooseStoreModal/DynamicChooseStoreModal'
import { DynamicGuestAddressModal } from '@/features/account/components/Modal/GuestAddressModal/DynamicGuestAddressModal'
import { Box, Column, Row } from '@shipt/design-system-layouts'
import { Body } from '@shipt/design-system-typography'
import { DynamicGlobalAddressModal } from '@/features/account/components/Modal/GlobalAddressModal/DynamicGlobalAddressModal'
import { routes } from '@shared/constants/routes'
import { useUserShoppingStore } from '@/features/shop/services/ShoppingStore/hooks'

export const StoreSelector = () => {
  const { data: storesAndPickups } = useQueryShoppingStores()
  const currentStoreID = useStoreId()
  const doesPickupStoreExist = storesAndPickups?.pickup_stores.some(
    (store) => store.id === currentStoreID,
  )
  const { openDialog } = useHandleDialog()
  const storeName = useUserStoreName()
  const isGuestUser = useIsGuestUser()
  const defaultShoppingAddress = useGlobalDefaultShoppingAddress()
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const isZipOnlyUser = useIsUserZipOnlySignedInUser()
  const { data: shoppingStore } = useUserShoppingStore()

  const ariaText = `Store selection button for ${storeName}. Click to change store.`

  const isPickup = useIsPickup()

  const handleButtonOnClick = (e: MouseEvent) => {
    e.preventDefault()
    if (isGuestUser) {
      return openDialog(DynamicGuestAddressModal)
    }
    buttonRef.current?.blur()
    trackElementClicked({
      type: 'button',
      link_name: 'select_delivery_address',
      display_format: 'horizontal_list',
    })
    if (isZipOnlyUser) {
      openDialog(DynamicGlobalAddressModal, {
        contentLabel: 'Enter your delivery address',
        subheading: 'Item availability and price may vary by location.',
        hideSavedAddresses: true,
        destinationUrl: routes.GLOBAL_HOMEPAGE.url,
      })
    } else {
      openDialog(DynamicChooseStoreModal)
    }
  }

  const address = isPickup
    ? (shoppingStore?.address_street_one ?? '')
    : (defaultShoppingAddress.street1 ?? defaultShoppingAddress.zip_code)

  return (
    <Row display={{ base: 'none', md: 'flex' }} spacing="md" align="center">
      <Column>
        <Body size="sm">{isPickup ? 'Pickup at' : 'Deliver to'}</Body>
        <Button onClick={handleButtonOnClick} aria-label={ariaText}>
          <Row as="u" align="center">
            <Body size="md" isPII>
              {address}
            </Body>
            <CaretDownIcon />
          </Row>
        </Button>
      </Column>
      {!!doesPickupStoreExist && (
        <Box display={{ base: 'none', lg: 'block' }}>
          <DeliveryPickupSwitch />
        </Box>
      )}
    </Row>
  )
}

const Button = styled(TextButton)`
  white-space: nowrap;
`
